import { LlmProvider, ProviderRequest, StreamEvent, Message } from '../types'

// Our normalized blocks already match Anthropic's shape except for `document`,
// which we expand into Anthropic's base64 source form here.
function toAnthropicMessages(messages: Message[]): any[] {
	return messages.map(m => ({
		role: m.role,
		content: m.content.map((b: any) => b.type === 'document'
			? { type: 'document', source: { type: 'base64', media_type: b.mediaType, data: b.data }, title: b.name }
			: b),
	}))
}

// Direct browser-to-Anthropic access using the user's own key (BYOK).
// No server/proxy involved, so there is no cost beyond the user's token spend.
// The `anthropic-dangerous-direct-browser-access` header opts in to CORS.
const ENDPOINT = 'https://api.anthropic.com/v1/messages'

export const anthropicProvider: LlmProvider = {
	id: 'anthropic',
	label: 'Anthropic (Claude)',
	models: [ 'claude-sonnet-5', 'claude-opus-4-8', 'claude-haiku-4-5' ],
	defaultModel: 'claude-sonnet-5',

	async *stream(req: ProviderRequest): AsyncGenerator<StreamEvent> {
		const body = {
			model: req.model,
			max_tokens: req.maxTokens ?? 4096,
			system: req.system,
			messages: toAnthropicMessages(req.messages),
			tools: req.tools,
			// Keep the chat snappy and cheap; flip to { type: 'adaptive' } to
			// let Claude reason across tool calls (also stream thinking then).
			thinking: { type: 'disabled' },
			stream: true,
		}

		let res: Response
		try {
			res = await fetch(ENDPOINT, {
				method: 'POST',
				headers: {
					'content-type': 'application/json',
					'x-api-key': req.apiKey,
					'anthropic-version': '2023-06-01',
					'anthropic-dangerous-direct-browser-access': 'true',
				},
				body: JSON.stringify(body),
				signal: req.signal,
			})
		}
		catch (e: any) {
			yield { type: 'error', error: 'Network error: ' + (e?.message ?? e) }
			return
		}

		if (!res.ok || !res.body) {
			const text = await res.text().catch(() => '')
			yield { type: 'error', error: `HTTP ${ res.status }: ${ text }` }
			return
		}

		const reader = res.body.getReader()
		const decoder = new TextDecoder()
		let buf = ''
		let stopReason = 'end_turn'

		while (true) {
			const { done, value } = await reader.read()
			if (done) break
			buf += decoder.decode(value, { stream: true })

			let sep
			while ((sep = buf.indexOf('\n\n')) !== -1) {
				const chunk = buf.slice(0, sep)
				buf = buf.slice(sep + 2)

				let dataStr = ''
				for (const line of chunk.split('\n')) {
					if (line.startsWith('data:')) dataStr += line.slice(5).trim()
				}
				if (!dataStr) continue

				let evt: any
				try { evt = JSON.parse(dataStr) }
				catch { continue }

				if (evt.type === 'content_block_start') {
					if (evt.content_block?.type === 'tool_use') {
						yield { type: 'tool_use_start', index: evt.index, id: evt.content_block.id, name: evt.content_block.name }
					}
				}
				else if (evt.type === 'content_block_delta') {
					if (evt.delta?.type === 'text_delta') {
						yield { type: 'text_delta', index: evt.index, text: evt.delta.text }
					}
					else if (evt.delta?.type === 'input_json_delta') {
						yield { type: 'tool_use_input_delta', index: evt.index, partialJson: evt.delta.partial_json }
					}
				}
				else if (evt.type === 'content_block_stop') {
					yield { type: 'block_stop', index: evt.index }
				}
				else if (evt.type === 'message_delta') {
					if (evt.delta?.stop_reason) stopReason = evt.delta.stop_reason
				}
				else if (evt.type === 'message_stop') {
					yield { type: 'done', stopReason }
				}
				else if (evt.type === 'error') {
					yield { type: 'error', error: evt.error?.message ?? 'stream error' }
				}
			}
		}
	},
}
